<script setup>
import { usePlatformApi } from '@/utils/platformApi'
import { requiredValidator } from '@core/utils/validators'

definePage({ meta: { layout: 'platform', public: true } })

const router = useRouter()
const route = useRoute()

const platformAccessToken = useCookie('platformAccessToken')

if (!platformAccessToken.value)
  await router.push('/platform/login')

const postId = route.params.id

const form = ref({
  title: '',
  slug: '',
  excerpt: '',
  content: '',
})

const currentStatus = ref('draft')
const currentImageUrl = ref(null)

const formRef = ref()
const fetchLoading = ref(true)
const fetchError = ref('')
const loading = ref(false)
const errors = ref({})
const successMessage = ref('')
const statusActionLoading = ref(false)
const statusActionError = ref('')

const handleAuthError = async err => {
  const status = err?.status ?? err?.response?.status
  if (status === 401 || status === 403) {
    platformAccessToken.value = null
    await router.push('/platform/login')

    return true
  }

  return false
}

const fetchPost = async () => {
  fetchLoading.value = true
  fetchError.value = ''
  try {
    const res = await usePlatformApi(`/api/v1/platform/blog-posts/${postId}`).get()
    const post = res?.data ?? res

    form.value = {
      title: post.title ?? '',
      slug: post.slug ?? '',
      excerpt: post.excerpt ?? '',
      content: post.content ?? '',
    }
    currentStatus.value = post.status
    currentImageUrl.value = post.featured_image_url
  } catch (err) {
    if (await handleAuthError(err))
      return

    fetchError.value = 'Gagal memuat artikel.'
    console.error('[Platform] fetch blog-post error', err)
  } finally {
    fetchLoading.value = false
  }
}

onMounted(fetchPost)

// ─── Gambar utama (ganti gambar yang sudah ada) ─────────────────────────────
const imageFile = ref(null)
const imagePreview = ref(null)
const uploadingImage = ref(false)

const handleImageChange = file => {
  if (!file) {
    imagePreview.value = null
    imageFile.value = null

    return
  }
  imageFile.value = file
  imagePreview.value = URL.createObjectURL(file)
}

const submit = async () => {
  const { valid } = await formRef.value?.validate()
  if (!valid)
    return

  loading.value = true
  errors.value = {}
  successMessage.value = ''

  try {
    const res = await usePlatformApi(`/api/v1/platform/blog-posts/${postId}`).put({ body: form.value })

    if (imageFile.value) {
      uploadingImage.value = true

      const fd = new FormData()

      fd.append('image', imageFile.value)

      try {
        const uploadRes = await usePlatformApi(`/api/v1/platform/blog-posts/${postId}/image`).post({ body: fd })
        const uploadedPost = uploadRes?.data ?? uploadRes

        currentImageUrl.value = uploadedPost?.featured_image_url ?? currentImageUrl.value
      } catch (uploadErr) {
        console.warn('[Platform] upload gambar artikel gagal', uploadErr)
      } finally {
        uploadingImage.value = false
        imageFile.value = null
        imagePreview.value = null
      }
    }

    successMessage.value = res?.message ?? 'Artikel berhasil diperbarui.'
  } catch (err) {
    if (await handleAuthError(err))
      return

    const status = err?.status ?? err?.response?.status
    const data = err?.data ?? err?.response?._data

    if (status === 422 && data?.errors)
      errors.value = data.errors
    else
      errors.value = { _general: [data?.message ?? 'Gagal memperbarui artikel.'] }
  } finally {
    loading.value = false
  }
}

const toggleStatus = async () => {
  statusActionLoading.value = true
  statusActionError.value = ''
  try {
    const action = currentStatus.value === 'published' ? 'unpublish' : 'publish'
    const res = await usePlatformApi(`/api/v1/platform/blog-posts/${postId}/${action}`).post()
    const post = res?.data ?? res

    currentStatus.value = post?.status ?? currentStatus.value
  } catch (err) {
    if (await handleAuthError(err))
      return

    const data = err?.data ?? err?.response?._data

    statusActionError.value = data?.message ?? 'Gagal mengubah status artikel.'
  } finally {
    statusActionLoading.value = false
  }
}
</script>

<template>
  <div class="pa-6">
    <div class="d-flex align-center justify-space-between mb-4 flex-wrap gap-3">
      <div class="d-flex align-center gap-3">
        <h4 class="text-h4 mb-0">
          Edit Artikel
        </h4>
        <VChip
          v-if="!fetchLoading"
          :color="currentStatus === 'published' ? 'success' : 'secondary'"
          size="small"
        >
          {{ currentStatus === 'published' ? 'Published' : 'Draft' }}
        </VChip>
      </div>
      <VBtn
        variant="outlined"
        :to="{ path: '/platform/blog-posts' }"
      >
        Kembali
      </VBtn>
    </div>

    <VCardText
      v-if="fetchLoading"
      class="pa-10 text-center"
    >
      <VProgressCircular
        indeterminate
        color="primary"
      />
    </VCardText>

    <VAlert
      v-else-if="fetchError"
      type="error"
      variant="tonal"
    >
      {{ fetchError }}
    </VAlert>

    <VForm
      v-else
      ref="formRef"
      @submit.prevent="submit"
    >
      <VRow>
        <VCol
          cols="12"
          md="8"
        >
          <VCard>
            <VCardText class="pa-5">
              <VAlert
                v-if="successMessage"
                type="success"
                variant="tonal"
                class="mb-4"
                closable
                @click:close="successMessage = ''"
              >
                {{ successMessage }}
              </VAlert>

              <VAlert
                v-if="errors._general"
                type="error"
                variant="tonal"
                class="mb-4"
              >
                {{ errors._general[0] }}
              </VAlert>

              <VTextField
                v-model="form.title"
                label="Judul Artikel *"
                class="mb-4"
                :rules="[requiredValidator]"
                :error-messages="errors.title"
              />

              <VTextField
                v-model="form.slug"
                label="Slug (URL)"
                prefix="/blog/"
                hint="Ubah dengan hati-hati -- link yang sudah dibagikan ke alamat lama akan berhenti berfungsi."
                persistent-hint
                class="mb-4"
                :error-messages="errors.slug"
              />

              <VTextarea
                v-model="form.excerpt"
                label="Ringkasan (opsional)"
                rows="2"
                class="mb-4"
                :error-messages="errors.excerpt"
              />

              <VTextarea
                v-model="form.content"
                label="Isi Artikel *"
                rows="16"
                :rules="[requiredValidator]"
                :error-messages="errors.content"
              />
            </VCardText>
          </VCard>
        </VCol>

        <VCol
          cols="12"
          md="4"
        >
          <VCard class="mb-4">
            <VCardTitle class="pt-4 px-4 text-body-1 font-weight-bold">
              Gambar Utama
            </VCardTitle>
            <VCardText class="pa-4">
              <div
                class="image-upload-area rounded-lg d-flex flex-column align-center justify-center pa-4 mb-3"
                :class="(imagePreview || currentImageUrl) ? '' : 'border-dashed'"
              >
                <VImg
                  v-if="imagePreview || currentImageUrl"
                  :src="imagePreview || currentImageUrl"
                  height="140"
                  cover
                  rounded="lg"
                />
                <div
                  v-else
                  class="text-center"
                >
                  <VIcon
                    icon="ri-image-add-line"
                    size="40"
                    color="medium-emphasis"
                  />
                  <p class="text-caption text-medium-emphasis mt-2 mb-0">
                    Belum ada gambar
                  </p>
                </div>
              </div>
              <VFileInput
                label="Ganti Gambar"
                accept="image/*"
                hide-details
                prepend-icon=""
                prepend-inner-icon="ri-upload-line"
                variant="outlined"
                :loading="uploadingImage"
                @update:model-value="handleImageChange"
              />
              <p class="text-caption text-medium-emphasis mt-1 mb-0">
                Maks. 2MB · JPG, PNG
              </p>
            </VCardText>
          </VCard>

          <VCard class="mb-4">
            <VCardText class="pa-4">
              <VAlert
                v-if="statusActionError"
                type="error"
                variant="tonal"
                density="compact"
                class="mb-3"
              >
                {{ statusActionError }}
              </VAlert>
              <VBtn
                block
                :color="currentStatus === 'published' ? 'warning' : 'success'"
                :loading="statusActionLoading"
                @click="toggleStatus"
              >
                {{ currentStatus === 'published' ? 'Tarik ke Draft' : 'Publish Sekarang' }}
              </VBtn>
              <p class="text-caption text-medium-emphasis mt-2 mb-0 text-center">
                {{
                  currentStatus === 'published'
                    ? 'Artikel sedang tampil di /blog. Menariknya ke draft langsung menyembunyikannya dari publik.'
                    : 'Artikel belum tampil publik. Publish untuk langsung menampilkannya di /blog.'
                }}
              </p>
            </VCardText>
          </VCard>

          <VBtn
            type="submit"
            color="primary"
            block
            size="large"
            :loading="loading"
          >
            Simpan Perubahan
          </VBtn>
        </VCol>
      </VRow>
    </VForm>
  </div>
</template>

<style scoped>
.image-upload-area {
  min-block-size: 140px;
  background: rgba(var(--v-theme-on-surface), 0.04);
}

.border-dashed {
  border: 2px dashed rgba(var(--v-border-color), var(--v-border-opacity));
}
</style>
